import { useMemo } from "react";
import { bool } from "prop-types";
import { observer } from "mobx-react";
import { uniq } from "lodash";
import { parsePrice, PriceFormats } from "@repo/utils";
import { CELL_WIDTHS } from "@utils";
import {
  useEstimateEditorSettings,
  useEstimateEditorStore,
  useEstimateStructure,
  useEstimateTableSettings,
  useVisibilityModeCellClassName,
  VisibilityButton,
  PinnableCell,
  DragHandleCell,
} from "@tools";
import { Tag } from "@components";
import { Collapse, Grid } from "@material-ui/core";
import { TimelineMenuTitle } from "../components/TimelineMenuTitle";
import useStyle from "./TimelineBilling.style";
import useCellStyle from "@tools/EstimateEditor/styles/CommonTableCellStyles.style";
import classnames from "classnames";

export const TimelineBilling = observer(({ isFirst }) => {
  
  const structure = useEstimateStructure();
  const { visibilityMode } = useEstimateEditorStore();
  const { blockExpansions, isPdfPrinterView } = useEstimateEditorSettings();
  const { fullSectionActions, billingChartWorkTypes } = useEstimateTableSettings();
  const classes = useStyle();
  const cellClasses = useCellStyle();

  const { timelineBillingScale, settings, visibility, workTypes } = structure;
  const { currencyObj, priceFormat } = settings;

  const { expandTimelineBilling } = settings;
  const { hideTimelineBilling } = visibility;
  
  const className = useVisibilityModeCellClassName(hideTimelineBilling);

  const scales = useMemo(() => (
    [...new Array(5).keys()].map(s => (
      timelineBillingScale.topValue / 4 * (4 - s)
    ))
  ), [timelineBillingScale.topValue]);

  const handleTimelineBillingVisibility = () => {
    if(blockExpansions) return;
    settings.setExpandTimelineBilling(!expandTimelineBilling);
  }

  const uniqueBillingChartWorkTypes = useMemo(() => (
    uniq(billingChartWorkTypes)
  ), [ billingChartWorkTypes ])

  const hasOverflowingWorkTypes = useMemo(() => (
    uniqueBillingChartWorkTypes.length > 8
  ), [ uniqueBillingChartWorkTypes ]);
  
  return (
    <>
      <TimelineMenuTitle
        code="billing"
        isFirst={isFirst}
        expandable
        expanded={expandTimelineBilling}
        onExpand={handleTimelineBillingVisibility}
        className={className}
        actions={visibilityMode &&
          <VisibilityButton
            hidden={hideTimelineBilling}
            onChange={visibility.setHideTimelineBilling}
          />
        }
      />
      <Collapse
        in={expandTimelineBilling}
        timeout="auto"
        mountOnEnter
        unmountOnExit
        className={className}
      >
        <Grid item container wrap="nowrap" className="bg-secondary preset-page">
          {
            !isPdfPrinterView && <DragHandleCell />
          }
          <PinnableCell
            hasStaticWidth
            allowOverflowDisplay
            tableDepth={1}
            wider={fullSectionActions}
            size="xLarge"
            className="pt-6 pb-2"
          >
            <Grid
              item container
              alignItems="flex-end"
              direction={hasOverflowingWorkTypes ? "row" : "column" }
              alignContent={hasOverflowingWorkTypes ? "flex-start" : "flex-end"}
              justifyContent={hasOverflowingWorkTypes ? "flex-end" : "flex-start"}
              className="h-full text-transform-none"
            >
              {
                Boolean(uniqueBillingChartWorkTypes?.length) &&
                workTypes
                  .filter(wT => uniqueBillingChartWorkTypes.includes(wT.id))
                  .map(wT => (
                    <Tag
                      key={wT.id}
                      text={wT.name}
                      color={wT.timelineColor}
                      className="m-0-5"
                      ellipsisOnOverflow forceDot
                      maxWidth={CELL_WIDTHS.VALUE.LARGE - 26}
                    />
                  ))
              }
            </Grid>
            <Grid
              item container
              direction="column"
              wrap="nowrap"
              className={classnames("w-max", classes.labelContainer)}
            >
              {
                scales.map((s, i) => (
                  <Grid
                    key={`scale${i}`}
                    item container
                    justifyContent="flex-end"
                    alignItems="center"
                    className={classnames("preset-tableTimelineBilling", cellClasses.headCell, classes.labelCell)}
                  >
                    { currencyObj.symbolStart }
                    { parsePrice(s, priceFormat === PriceFormats.AMERICAN) }
                    { currencyObj.symbolEnd ? " " + currencyObj.symbolEnd : undefined }
                  </Grid>
                ))
              }
            </Grid>
          </PinnableCell>
        </Grid>
      </Collapse>
    </>
  );
});

TimelineBilling.propTypes = {
  isFirst: bool,
};
